import { usePrefillData } from '@/hooks/prefillData/usePrefillData';
import { gql, useMutation, useQuery, useSuspenseQuery } from '@apollo/client';
import { Button, Grid, Typography } from '@mui/material';
import _ from 'lodash';
import moment, { Moment } from 'moment-timezone';
import { useLayoutEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';

import { CompetingVisitsMap } from '../../../components/availability/AvailabilitySelector/CompetingVisitsMap';
import { TimeButtonMoment } from '../../../components/buttons/TimeButtons';
import { ClinicianChip } from '../../../components/clinicians/ClinicianChip';
import { AxleInfoField } from '../../../components/displayFields/AxleInfoField';
import { ChipsInRows, QhpChip } from '../../../components/displayFields/chips';
import { AxleDateCalendar } from '../../../components/formFields/datetimeFields';
import { AxleDialogPrimaryButton } from '../../../components/layout/AxleDialog';
import { PatientTimeslotPreferenceChips } from '../../../components/patients/PatientTimeslotPreferenceChip';
import { QhpChipWithUtilization } from '../../../components/utilization/QhpChipWithUtilization';
import { If } from '../../../components/utils/conditionalComponents';
import {
    HorizontalStack,
    VerticalSpace,
    VerticalStack,
} from '../../../components/utils/linearLayouts';
import { UNASSIGNED_QHP_OBJECT } from '../../../constants/visit';
import { useAppConfig } from '../../../fetching/api/appConfig';
import { useVisitAvailabilityMetricsByDate } from '../../../fetching/api/availability';
import { usePatient } from '../../../fetching/api/patients';
import { QhpSimple } from '../../../fetching/sharedSchemas/basicSchemas';
import { successToast } from '../../../toast';
import { FeatureFlag } from '../../../types/featureFlag';
import { useBooleanState } from '../../../utils/booleanState';
import { earlierMoment, laterMoment } from '../../../utils/datetimes/comparisons';
import { MAXIMUM_DATE, MINIMUM_DATE } from '../../../utils/datetimes/misc';
import { getEndOfMonth, getStartOfMonth } from '../../../utils/datetimes/monthStarts';
import { getUserTimezone } from '../../../utils/datetimes/timezone';
import { TimeWindow } from '../../../utils/datetimes/types';
import { WeekdayTimeWindow } from '../../../utils/datetimes/weekdays';
import { InputState, useInputState } from '../../../utils/inputState';
import { useConfigValue } from '../../../utils/useConfigValue';
import { useFeatureFlagEnabled } from '../../../utils/useFeatureFlagEnabled';
import { useVisitBookingPageContext } from '../VisitBookingPageProvider';
import { FlexibleSchedulingDialog } from './FlexibleSchedulingDialog';

export const VisitAvailabilityFragment = gql`
    fragment VisitAvailabilityFragment on Availability {
        potentialAppointmentAssignments {
            clinician {
                id
                firstName
                lastName
            }
            clinicianAppointmentId
        }
        timeOptions {
            start
            end
        }
        timezone
    }
`;

export type AvailabilityType = {
    potentialAppointmentAssignments: {
        clinician: {
            id: string;
            firstName: string;
            lastName: string;
        } | null;
        clinicianAppointmentId: string;
    }[];
    timeOptions: {
        start: string;
        end: string;
    }[];
    timezone: string;
};

export const GET_CONFIRMED_AVAILABILITY_QUERY = gql`
    query GetConfirmedVisitAvailability(
        $input: ConfirmedAvailabilityInput!
        $filter: AvailabilityFilter!
    ) {
        confirmedAvailability(input: $input, filter: $filter) {
            ...VisitAvailabilityFragment
        }
    }
`;

export type ConfirmedAvailabilityQueryType = {
    confirmedAvailability: AvailabilityType[];
};

// TODO: Refactor these types to be somewhere more central.
export type PotentialJointAssignment = {
    clinician: QhpSimple;
    clinicianAppointmentId: string;
};
export type PotentialJointAssignmentAndTime = {
    potentialAppointmentAssignments: PotentialJointAssignment[];
    timeOption: {
        start: Moment;
        end: Moment;
    };
    timezone: string;
};
export type PotentialJointAssignmentAndTimes = {
    potentialAppointmentAssignments: PotentialJointAssignment[];
    timeOptions: {
        start: Moment;
        end: Moment;
    }[];
    timezone: string;
};

export type VisitBookingTimeSelections = Record<
    string,
    {
        confirmed: PotentialJointAssignmentAndTime | null;
        unconfirmed: TimeWindow[];
    }
>;

export const CREATE_CONFIRMED_VISIT_MUTATION = gql`
    mutation CreateConfirmedVisit($input: BetaCreateConfirmedVisitInput!) {
        betaCreateConfirmedVisit(input: $input) {
            id
        }
    }
`;

export type CreateConfirmedVisitMutationType = {
    betaCreateConfirmedVisit: {
        id: string;
    };
};

export const CREATE_CONFIRMED_VISIT_FROM_VISIT_REQUEST_MUTATION = gql`
    mutation CreateConfirmedVisitFromVisitRequest(
        $input: CreateConfirmedVisitFromVisitRequestInput!
    ) {
        createConfirmedVisitFromVisitRequest(input: $input) {
            id
        }
    }
`;

export type CreateConfirmedVisitFromVisitRequestMutationType = {
    createConfirmedVisitFromVisitRequest: {
        id: string;
    };
};

const GET_PATIENT_FOR_CONFIRMED_VISIT_BOOKING_FLOW = gql`
    query GetPatientForConfirmedVisitBookingFlow($patientId: UUID!) {
        patient(id: $patientId) {
            id
            preferredTimeslots {
                day
                startTime
                endTime
            }
        }
    }
`;

type GetPatientForConfirmedVisitBookingFlowQueryType = {
    patient: {
        id: string;
        preferredTimeslots: WeekdayTimeWindow[];
    } | null;
};

const ConfirmedAvailOptions = ({
    options,
    timezone,
    isConfirmedTimeChosen,
    onSelectConfirmedTime,
}: {
    options: PotentialJointAssignmentAndTimes[];
    timezone: string;
    isConfirmedTimeChosen: (timeWindowAssignment: PotentialJointAssignmentAndTime) => boolean;
    onSelectConfirmedTime: (timeWindowAssignment: PotentialJointAssignmentAndTime) => void;
}) => {
    const appConfig = useAppConfig();
    return (
        <VerticalStack spacing={2}>
            {options.map((jointAssignmentAndTimes, optionIdx) => (
                <VerticalStack spacing={1} key={optionIdx}>
                    {/* Show assignment. */}
                    {/* TODO: Consider more clearly indication which position will be filled by which QHP. */}
                    <ChipsInRows>
                        {Object.values(jointAssignmentAndTimes.potentialAppointmentAssignments).map(
                            (position) =>
                                appConfig.should_display_maps_and_metrics_in_booking ? (
                                    <QhpChipWithUtilization
                                        key={position.clinician.uuid}
                                        qhp={position.clinician}
                                        showLink={true}
                                    />
                                ) : (
                                    <QhpChip
                                        key={position.clinician.uuid}
                                        qhp={position.clinician}
                                        showLink={true}
                                    />
                                ),
                        )}
                    </ChipsInRows>
                    {/* Show times for assignment. */}
                    <HorizontalStack spacing={2} flexWrap={'wrap'} useFlexGap={true}>
                        {jointAssignmentAndTimes.timeOptions.map((timeOption, windowIdx) => (
                            <TimeButtonMoment
                                startTime={timeOption.start}
                                endTime={null}
                                minWidth={150}
                                selected={isConfirmedTimeChosen({
                                    potentialAppointmentAssignments:
                                        jointAssignmentAndTimes.potentialAppointmentAssignments,
                                    timeOption: timeOption,
                                    timezone: timezone,
                                })}
                                timezone={timezone}
                                onClick={() =>
                                    onSelectConfirmedTime({
                                        potentialAppointmentAssignments:
                                            jointAssignmentAndTimes.potentialAppointmentAssignments,
                                        timeOption: timeOption,
                                        timezone: timezone,
                                    })
                                }
                                key={windowIdx}
                            />
                        ))}
                    </HorizontalStack>
                </VerticalStack>
            ))}
        </VerticalStack>
    );
};

/**
 * Calculates the date range for a month, constrained by the visit request fulfillment window.
 * Returns null if the calculated range is invalid (start after end).
 */
const calculateMonthDateRange = (
    currentMonth: Moment,
    monthsAhead: number,
    visitRequestWindow?: { startDate: Date | null; endDate: Date | null } | null,
): { startDatetime: Moment; endDatetime: Moment } | null => {
    const monthStart = getStartOfMonth(currentMonth.clone().add(monthsAhead, 'months'));
    const monthEnd = getEndOfMonth(currentMonth.clone().add(monthsAhead, 'months'));

    const constrainedStart = laterMoment(
        monthStart,
        moment(visitRequestWindow?.startDate ?? MINIMUM_DATE),
    );
    const constrainedEnd = earlierMoment(
        monthEnd,
        moment(visitRequestWindow?.endDate ?? MAXIMUM_DATE),
    );

    if (constrainedStart.isAfter(constrainedEnd)) {
        return null;
    }

    return { startDatetime: constrainedStart, endDatetime: constrainedEnd };
};

/**
 * Groups time options by date, converting ISO strings to moment objects with timezone.
 * Each time option is only converted once for efficiency.
 */
const groupTimeOptionsByDate = (
    timeOptions: { start: string; end: string }[],
    timezone: string,
): Record<string, { start: Moment; end: Moment }[]> => {
    const timeOptionsByDate: Record<string, { start: Moment; end: Moment }[]> = {};

    for (const timeOption of timeOptions) {
        const startMoment = moment(timeOption.start).tz(timezone);
        const endMoment = moment(timeOption.end).tz(timezone);
        const dateKey = startMoment.format('YYYY-MM-DD');

        if (!(dateKey in timeOptionsByDate)) {
            timeOptionsByDate[dateKey] = [];
        }
        timeOptionsByDate[dateKey].push({
            start: startMoment,
            end: endMoment,
        });
    }

    return timeOptionsByDate;
};

/**
 * Transforms API clinician data into QhpSimple format for internal use.
 * Returns UNASSIGNED_QHP_OBJECT if clinician is null.
 */
const transformClinicianToQhpSimple = (
    clinician: { id: string; firstName: string; lastName: string } | null,
    timezone: string,
): QhpSimple => {
    if (!clinician) {
        return UNASSIGNED_QHP_OBJECT;
    }

    return {
        uuid: clinician.id,
        name: `${clinician.firstName} ${clinician.lastName}`,
        first_name: clinician.firstName,
        last_name: clinician.lastName,
        logo_url: null,
        timezone: timezone,
        teams: [],
        address: null,
    };
};

/**
 * Hook to preload confirmed availability for future months.
 * Uses cache-and-network policy to return cached data immediately while
 * refreshing in the background to ensure data freshness.
 *
 * @param monthsToPreload - Number of months ahead to preload (1-2, default: 2)
 * @param currentMonth - The current calendar month being viewed
 * @param baseInput - The base query input (patient, address, service info)
 * @param visitRequestWindow - Optional fulfillment window to constrain date ranges
 */
const usePreloadConfirmedAvailability = ({
    monthsToPreload = 2,
    currentMonth,
    baseInput,
    visitRequestWindow,
}: {
    monthsToPreload?: 1 | 2;
    currentMonth: Moment;
    baseInput: {
        patientId: string;
        address: {
            line1?: string | null;
            line2?: string | null;
            city?: string | null;
            state?: string | null;
            zipCode?: string | null;
        };
        serviceAreaId: string;
        serviceBundleIds: string[] | null;
        appointments?: { appointmentId: string; serviceIds: string[] }[];
    };
    visitRequestWindow?: { startDate: Date | null; endDate: Date | null } | null;
}) => {
    // Calculate date ranges for months to preload
    const month1Range = calculateMonthDateRange(currentMonth, 1, visitRequestWindow);
    const month2Range = calculateMonthDateRange(currentMonth, 2, visitRequestWindow);

    // Preload month 1 (always enabled if monthsToPreload >= 1)
    // Using cache-first to efficiently warm the cache without redundant network requests
    useQuery<ConfirmedAvailabilityQueryType>(GET_CONFIRMED_AVAILABILITY_QUERY, {
        variables: {
            input: baseInput,
            filter: month1Range ?? { startDatetime: MINIMUM_DATE, endDatetime: MINIMUM_DATE },
        },
        skip: !month1Range || monthsToPreload < 1,
        fetchPolicy: 'cache-first',
    });

    // Preload month 2 (enabled if monthsToPreload >= 2)
    useQuery<ConfirmedAvailabilityQueryType>(GET_CONFIRMED_AVAILABILITY_QUERY, {
        variables: {
            input: baseInput,
            filter: month2Range ?? { startDatetime: MINIMUM_DATE, endDatetime: MINIMUM_DATE },
        },
        skip: !month2Range || monthsToPreload < 2,
        fetchPolicy: 'cache-first',
    });
};

export const BookConfirmedVisitTab = ({
    selectedTimeState,
}: {
    selectedTimeState: InputState<PotentialJointAssignmentAndTime | null>;
}) => {
    ////////////////////////////////////////////////////////////////////////////
    // Booking context
    ////////////////////////////////////////////////////////////////////////////
    const {
        patientId,
        address,
        serviceAreaId,
        serviceBundleId,
        visitRequest,
        setSelectedDate,
        setTimezone: setContextTimezone,
        setAvailableClinicianIds,
    } = useVisitBookingPageContext();
    const bookingFlowMapEnabled = useFeatureFlagEnabled(FeatureFlag.SHOW_MAP_IN_BOOKING_FLOW);
    const columnSize = bookingFlowMapEnabled ? 6 : 4;
    const patient = usePatient(patientId);
    const { data: patientData } = useSuspenseQuery<GetPatientForConfirmedVisitBookingFlowQueryType>(
        GET_PATIENT_FOR_CONFIRMED_VISIT_BOOKING_FLOW,
        {
            variables: {
                patientId,
            },
        },
    );
    const patientTimeslotPreferences = patientData.patient?.preferredTimeslots ?? [];

    ////////////////////////////////////////////////////////////////////////////
    // Date-related States
    ////////////////////////////////////////////////////////////////////////////
    const calendarMonthViewState = useInputState(moment());
    const selectedDateState = useInputState<Moment | null>(null, { required: true });

    // TODO(jessica): Review this logic + use case + user experience.
    const [startDatetime, endDatetime] = useMemo(() => {
        const monthStart = getStartOfMonth(calendarMonthViewState.value);
        const monthEnd = getEndOfMonth(calendarMonthViewState.value);
        const visitRequestEarliestStart = visitRequest?.fulfillmentWindow?.startDate
            ? moment(visitRequest.fulfillmentWindow.startDate).startOf('day')
            : MINIMUM_DATE;
        const visitRequestLatestEnd = visitRequest?.fulfillmentWindow?.endDate
            ? moment(visitRequest.fulfillmentWindow.endDate).endOf('day').toDate()
            : MAXIMUM_DATE;
        const calculatedStart = laterMoment(monthStart, moment(visitRequestEarliestStart));
        const calculatedEnd = earlierMoment(monthEnd, moment(visitRequestLatestEnd));
        if (calculatedStart.isAfter(calculatedEnd)) {
            return [MINIMUM_DATE, MINIMUM_DATE];
        }
        return [calculatedStart, calculatedEnd];
    }, [calendarMonthViewState.value, visitRequest]);

    ////////////////////////////////////////////////////////////////////////////
    // Get availability data
    ////////////////////////////////////////////////////////////////////////////
    const { data: availabilityData } = useSuspenseQuery<ConfirmedAvailabilityQueryType>(
        GET_CONFIRMED_AVAILABILITY_QUERY,
        {
            variables: {
                input: {
                    // Who
                    patientId,
                    // Where
                    address: {
                        line1: address?.line1,
                        line2: address?.line2,
                        city: address?.city,
                        state: address?.state,
                        zipCode: address?.zipCode,
                    },
                    serviceAreaId,
                    // What
                    serviceBundleIds: serviceBundleId === '' ? null : [serviceBundleId],
                    appointments: visitRequest?.appointments.map((appointment) => {
                        return {
                            appointmentId: appointment.id,
                            serviceIds: appointment.services.map((service) => service.id),
                        };
                    }),
                },
                filter: {
                    // When
                    startDatetime,
                    endDatetime,
                },
            },
            fetchPolicy: 'cache-and-network',
        },
    );

    // Preload the next 2 months of availability
    usePreloadConfirmedAvailability({
        monthsToPreload: 2,
        currentMonth: calendarMonthViewState.value,
        baseInput: {
            patientId,
            address: {
                line1: address?.line1,
                line2: address?.line2,
                city: address?.city,
                state: address?.state,
                zipCode: address?.zipCode,
            },
            serviceAreaId,
            serviceBundleIds: serviceBundleId === '' ? null : [serviceBundleId],
            appointments: visitRequest?.appointments.map((appointment) => ({
                appointmentId: appointment.id,
                serviceIds: appointment.services.map((service) => service.id),
            })),
        },
        visitRequestWindow: visitRequest?.fulfillmentWindow,
    });

    const showMetricsByDate = useConfigValue('should_display_metrics_by_date_in_booking');
    const availabilityMetricsByDate = useVisitAvailabilityMetricsByDate({
        enabled: showMetricsByDate,
        startDate: getStartOfMonth(calendarMonthViewState.value),
        endDate: getEndOfMonth(calendarMonthViewState.value),
        patientId: patient.uuid,
        addressId: address?.id ?? '',
        serviceAreaId,
        serviceBundleId,
        serviceIds:
            visitRequest?.appointments
                .map((appointment) => appointment.services)
                .flatMap((services) => services.map((service) => service.id)) ?? [],
    }) ?? { availabilityByDate: {} };

    /* eslint-disable @typescript-eslint/no-unnecessary-condition */
    const timezone =
        availabilityData.confirmedAvailability[0]?.timezone ??
        address?.timezone ??
        getUserTimezone();
    /* eslint-enable @typescript-eslint/no-unnecessary-condition */

    // Write selected date and timezone to context for the booking flow map.
    // useLayoutEffect ensures values propagate before paint, so
    // BookingFlowMapInner (which also reads in useLayoutEffect) sees them
    // on the same commit rather than one frame later.
    useLayoutEffect(() => {
        if (!bookingFlowMapEnabled) return;
        setSelectedDate(selectedDateState.value);
    }, [bookingFlowMapEnabled, selectedDateState.value, setSelectedDate]);
    useLayoutEffect(() => {
        if (!bookingFlowMapEnabled) return;
        setContextTimezone(timezone);
    }, [bookingFlowMapEnabled, timezone, setContextTimezone]);

    const confirmedAvailByDate = useMemo(() => {
        const confirmedAvailByDate: Record<string, PotentialJointAssignmentAndTimes[]> = {};

        for (const jointAssignmentAndTimes of availabilityData.confirmedAvailability) {
            // Group time options by date (converts to moments with timezone once)
            const timeOptionsByDate = groupTimeOptionsByDate(
                jointAssignmentAndTimes.timeOptions,
                timezone,
            );

            // Transform clinician assignments once (reused for all dates)
            const potentialAppointmentAssignments =
                jointAssignmentAndTimes.potentialAppointmentAssignments.map((position) => ({
                    clinicianAppointmentId: position.clinicianAppointmentId,
                    clinician: transformClinicianToQhpSimple(
                        position.clinician,
                        jointAssignmentAndTimes.timezone,
                    ),
                }));

            // Add transformed data for each date
            for (const dateKey of Object.keys(timeOptionsByDate)) {
                if (!(dateKey in confirmedAvailByDate)) {
                    confirmedAvailByDate[dateKey] = [];
                }
                confirmedAvailByDate[dateKey].push({
                    potentialAppointmentAssignments,
                    timeOptions: timeOptionsByDate[dateKey],
                    timezone: timezone,
                });
            }
        }

        return confirmedAvailByDate;
    }, [availabilityData.confirmedAvailability, timezone]);

    const datesWithConfirmedAvail = Object.keys(confirmedAvailByDate).map((dateString: string) =>
        moment(dateString, 'YYYY-MM-DD'),
    );

    const preferredClinicians = useMemo(() => {
        if (!visitRequest) return [];
        return (
            visitRequest.appointments
                .filter((appointment) => appointment.clinician)
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                .map((appointment) => appointment.clinician!)
        );
    }, [visitRequest]);

    ////////////////////////////////////////////////////////////////////////////
    // Manage selections
    ////////////////////////////////////////////////////////////////////////////
    const confirmedAvailToday: PotentialJointAssignmentAndTimes[] = useMemo(
        () =>
            selectedDateState.value &&
            selectedDateState.value.format('YYYY-MM-DD') in confirmedAvailByDate
                ? confirmedAvailByDate[selectedDateState.value.format('YYYY-MM-DD')]
                : [],
        [selectedDateState.value, confirmedAvailByDate],
    );

    // Write eligible clinician IDs to context for the booking flow map.
    // useLayoutEffect to match the date/timezone effects above.
    // Filter out UNASSIGNED_QHP_OBJECT ('None') — it's not a real clinician UUID.
    useLayoutEffect(() => {
        if (!bookingFlowMapEnabled) return;
        const ids = [
            ...new Set(
                confirmedAvailToday.flatMap((a) =>
                    a.potentialAppointmentAssignments
                        .map((p) => p.clinician.uuid)
                        .filter((id) => id !== UNASSIGNED_QHP_OBJECT.uuid),
                ),
            ),
        ];
        setAvailableClinicianIds(ids);
    }, [bookingFlowMapEnabled, confirmedAvailToday, setAvailableClinicianIds]);

    const chosenDate = useMemo(
        () => selectedTimeState.value?.timeOption.start,
        [selectedTimeState.value],
    );

    ////////////////////////////////////////////////////////////////////////////
    // Helper functions
    ////////////////////////////////////////////////////////////////////////////
    // Split confirmed availability into care team and non-care team.
    const isQhpInCareTeam = (qhp: QhpSimple) =>
        patient.care_team.some((qhpInCareTeam) => qhpInCareTeam.uuid === qhp.uuid);
    const areAllQhpsInAssignmentInCareTeam = (
        potentialJointAssignment: PotentialJointAssignmentAndTimes,
    ) =>
        potentialJointAssignment.potentialAppointmentAssignments
            .map((position) => position.clinician)
            .every(isQhpInCareTeam);
    const [careTeamConfirmedAvailToday, nonCareTeamConfirmedAvailToday] = _.partition(
        confirmedAvailToday,
        areAllQhpsInAssignmentInCareTeam,
    );

    // Selecting
    const isConfirmedTimeChosen = (potentialJointAssignment: PotentialJointAssignmentAndTime) => {
        // The moments need to be formatted into strings because otherwise, we will be
        // comparing moment objects which may not necessarily be the same moment. This
        // happens when switching between the confirmed/unconfirmed tabs.
        const sameStart =
            potentialJointAssignment.timeOption.start.format('YYYY-MM-DDTHH:mmZ') ===
            selectedTimeState.value?.timeOption.start.format('YYYY-MM-DDTHH:mmZ');
        const sameEnd =
            potentialJointAssignment.timeOption.end.format('YYYY-MM-DDTHH:mmZ') ===
            selectedTimeState.value?.timeOption.end.format('YYYY-MM-DDTHH:mmZ');
        const sameClinicians = potentialJointAssignment.potentialAppointmentAssignments.every(
            (appointmentAssignment, idx) =>
                appointmentAssignment.clinician.uuid ===
                selectedTimeState.value?.potentialAppointmentAssignments[idx].clinician.uuid,
        );
        return sameStart && sameEnd && sameClinicians;
    };

    const onSelectConfirmedTime = (potentialJointAssignment: PotentialJointAssignmentAndTime) => {
        selectedTimeState.setInputState(potentialJointAssignment);
    };

    // More options
    const [
        isFlexibleSchedulingDialogOpen,
        openFlexibleSchedulingDialog,
        closeFlexibleSchedulingDialog,
    ] = useBooleanState(false);

    // Submitting
    const { visitPrefillInfo } = usePrefillData();
    const history = useHistory();
    const [
        createConfirmedVisitFromVisitRequest,
        { loading: createConfirmedVisitFromVisitRequestLoading },
    ] = useMutation<CreateConfirmedVisitFromVisitRequestMutationType>(
        CREATE_CONFIRMED_VISIT_FROM_VISIT_REQUEST_MUTATION,
    );
    const [createConfirmedVisit, { loading: createConfirmedVisitLoading, client }] =
        useMutation<CreateConfirmedVisitMutationType>(CREATE_CONFIRMED_VISIT_MUTATION);
    const isLoading = createConfirmedVisitFromVisitRequestLoading || createConfirmedVisitLoading;

    const onSubmit = () => {
        if (isLoading) return;
        if (!selectedTimeState.value) return;
        if (visitRequest) {
            void createConfirmedVisitFromVisitRequest({
                variables: {
                    input: {
                        visitRequestId: visitRequest.id,
                        // Who
                        patientId: patientId,
                        serviceAssignments:
                            selectedTimeState.value.potentialAppointmentAssignments.map(
                                (position) => ({
                                    clinicianId: position.clinician.uuid,
                                    serviceIds: visitRequest.appointments
                                        .map((appointment) => appointment.services)
                                        .flatMap((services) =>
                                            services.map((service) => service.id),
                                        ),
                                }),
                            ),
                        // What
                        serviceIds: visitRequest.appointments
                            .map((appointment) => appointment.services)
                            .flatMap((services) => services.map((service) => service.id)),
                        // Where
                        serviceAreaId: serviceAreaId,
                        address: {
                            line1: address?.line1,
                            line2: address?.line2,
                            city: address?.city,
                            state: address?.state,
                            zipCode: address?.zipCode,
                        },
                        // When
                        startDatetime:
                            selectedTimeState.value.timeOption.start.format('YYYY-MM-DDTHH:mm:ssZ'),
                    },
                },
                onCompleted: (data) => {
                    const visitId = data.createConfirmedVisitFromVisitRequest.id;
                    client.cache.evict({ fieldName: 'visitRequest' });
                    successToast('Visit created!');
                    history.push(`/visits/${visitId}`);
                },
            });
        } else {
            void createConfirmedVisit({
                variables: {
                    input: {
                        // Who
                        patientId: patientId,
                        serviceBundleAssignments:
                            selectedTimeState.value.potentialAppointmentAssignments.map(
                                (position) => ({
                                    clinicianId: position.clinician.uuid,
                                    serviceBundlePositionId: position.clinicianAppointmentId,
                                }),
                            ),
                        // What
                        ...(serviceBundleId === '' ? {} : { serviceBundleId: serviceBundleId }),
                        // Where
                        serviceAreaId: serviceAreaId,
                        address: {
                            line1: address?.line1,
                            line2: address?.line2,
                            city: address?.city,
                            state: address?.state,
                            zipCode: address?.zipCode,
                        },
                        // When
                        startDatetime:
                            selectedTimeState.value.timeOption.start.format('YYYY-MM-DDTHH:mm:ssZ'),
                        // Misc
                        metadata: visitPrefillInfo?.metadata,
                        notesForClinician: visitPrefillInfo?.notes_for_clinician,
                    },
                },
                onCompleted: (data) => {
                    const visitId = data.betaCreateConfirmedVisit.id;
                    successToast('Visit created!');
                    history.push(`/visits/${visitId}`);
                },
            });
        }
    };

    return (
        <>
            {patientTimeslotPreferences.length > 0 && (
                <VerticalStack spacing={1}>
                    <Typography variant={'h3'}>Patient preferred availability:</Typography>
                    <PatientTimeslotPreferenceChips
                        patientTimeslotPreferences={patientTimeslotPreferences}
                    />
                </VerticalStack>
            )}
            {preferredClinicians.length > 0 && (
                <AxleInfoField label={'Preferred Clinician(s)'}>
                    {preferredClinicians.map((clinician) => (
                        <ClinicianChip
                            key={clinician.id}
                            clinician={{
                                id: clinician.id,
                                firstName: clinician.firstName,
                                lastName: clinician.lastName,
                                logoUrl: null,
                            }}
                            showLink={true}
                        />
                    ))}
                </AxleInfoField>
            )}
            <FlexibleSchedulingDialog
                isOpen={isFlexibleSchedulingDialogOpen}
                close={closeFlexibleSchedulingDialog}
                timezone={timezone}
            />
            <Grid container spacing={bookingFlowMapEnabled ? 3 : 0}>
                <Grid item xs={12} lg={columnSize}>
                    <VerticalStack>
                        <AxleDateCalendar
                            calendarMonthViewState={calendarMonthViewState}
                            selectedDateState={selectedDateState}
                            datesWithAvailability={datesWithConfirmedAvail}
                            availabilityMetricsByDate={availabilityMetricsByDate}
                            chosenDates={chosenDate ? [chosenDate] : []}
                            timezone={timezone}
                            size={'lg'}
                        />
                        <Button
                            variant={'text'}
                            color={'primary'}
                            onClick={openFlexibleSchedulingDialog}
                        >
                            {'More options'}
                        </Button>
                    </VerticalStack>
                </Grid>
                {selectedDateState.value ? (
                    <>
                        {/* If care teams are supplied, show confirmed avail split out by care team and not. */}
                        <Grid item xs={12} lg={columnSize}>
                            <If condition={patient.care_team.length > 0}>
                                <Typography variant={'h3'} mb={2}>
                                    Recommended Clinicians
                                </Typography>
                                <ConfirmedAvailOptions
                                    options={careTeamConfirmedAvailToday}
                                    timezone={timezone}
                                    isConfirmedTimeChosen={isConfirmedTimeChosen}
                                    onSelectConfirmedTime={onSelectConfirmedTime}
                                />
                                <If condition={careTeamConfirmedAvailToday.length === 0}>
                                    No care team clinicians available on selected date.
                                </If>
                                <VerticalSpace spacing={2} />
                                <Typography variant='h3' mb={2}>
                                    Other Eligible Clinicians
                                </Typography>
                                <ConfirmedAvailOptions
                                    options={nonCareTeamConfirmedAvailToday}
                                    timezone={timezone}
                                    isConfirmedTimeChosen={isConfirmedTimeChosen}
                                    onSelectConfirmedTime={onSelectConfirmedTime}
                                />
                                <If condition={nonCareTeamConfirmedAvailToday.length === 0}>
                                    No other eligible clinicians available on selected date.
                                </If>
                            </If>
                            {/* Without care teams, show all confirmed avail together. */}
                            <If condition={patient.care_team.length === 0}>
                                <Typography variant='h3' mb={2}>
                                    Eligible Clinicians
                                </Typography>
                                <ConfirmedAvailOptions
                                    options={confirmedAvailToday}
                                    timezone={timezone}
                                    isConfirmedTimeChosen={isConfirmedTimeChosen}
                                    onSelectConfirmedTime={onSelectConfirmedTime}
                                />
                                <If condition={confirmedAvailToday.length === 0}>
                                    No eligible clinicians available on selected date.
                                </If>
                            </If>
                        </Grid>
                    </>
                ) : (
                    <Grid item xs={12} lg={columnSize}>
                        <Typography variant={'body1'}>
                            Select a date to view availability.
                        </Typography>
                    </Grid>
                )}
                {!bookingFlowMapEnabled && (
                    <Grid
                        item
                        xs={12}
                        lg={4}
                        mt={2}
                        pl={{ xs: 0, lg: 2 }}
                        sx={{ flex: '1', maxHeight: '400px', minHeight: '200px' }}
                    >
                        {selectedDateState.value && (
                            <CompetingVisitsMap
                                patientId={patientId}
                                addressId={address?.id ?? ''}
                                serviceAreaId={serviceAreaId}
                                serviceBundleId={serviceBundleId}
                                serviceIds={
                                    visitRequest?.appointments
                                        .map((appointment) => appointment.services)
                                        .flatMap((services) =>
                                            services.map((service) => service.id),
                                        ) ?? []
                                }
                                date={selectedDateState.value}
                                timezone={timezone}
                            />
                        )}
                    </Grid>
                )}
            </Grid>
            <VerticalSpace spacing={2} />
            <AxleDialogPrimaryButton
                label={`${
                    selectedTimeState.value !== null
                        ? `Book on ${selectedTimeState.value.timeOption.start.format(
                              'dddd, MMMM D',
                          )} at ${selectedTimeState.value.timeOption.start.format('h:mm a')}`
                        : 'Book'
                }`}
                enabled={!isLoading && selectedTimeState.value !== null}
                onClick={onSubmit}
            />
        </>
    );
};
